import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { integrationsDataCards, integrationsDataTabs } from '@/data/integrationsData';
import { useCompanies } from '@/hooks/api/integrations/useCompanies';
import { useIntegrations } from '@/hooks/api/integrations/useIntegrations';
import { renderIntegrationForm } from '@/components/integrations/integrationForms';
import { Loader2, ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface VSLeadsIntegrationsProps {
  videoId?: string;
  strategyId?: string;
}

export function VSLeadsIntegrations({ videoId, strategyId }: VSLeadsIntegrationsProps) {
  useLanguage();
  const { data: companies, isLoading: isLoadingCompanies } = useCompanies();
  const { data: userIntegrations, isLoading: isLoadingIntegrations } = useIntegrations('leads');

  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');

  // Mapear dados de integrações com status de conexão
  const integrationCards = useMemo(() => {
    if (!companies || !userIntegrations) {
      return integrationsDataCards.map(card => ({
        ...card,
        isConnected: false,
        companyData: null,
      }));
    }

    return integrationsDataCards.map((card) => {
      const companyData = companies.find((c) => c.name.toLowerCase() === card.name.toLowerCase());

      // Verificar se há integrações ativas para esta empresa
      const hasActiveIntegration = userIntegrations.some(
        (integration) =>
          integration.id_company === companyData?.id &&
          integration.status === 'active'
      );

      return {
        ...card,
        isConnected: hasActiveIntegration,
        companyData,
      };
    });
  }, [companies, userIntegrations]);

  // Filtrar integrações por busca e tab
  const filteredIntegrations = useMemo(() => {
    return integrationCards.filter((integration) => {
      const matchesSearch = integration.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesTab =
        selectedTab === 'all' ||
        (selectedTab === 'active' && integration.isConnected) ||
        (selectedTab === 'inactive' && !integration.isConnected);

      return matchesSearch && matchesTab;
    });
  }, [integrationCards, searchTerm, selectedTab]);

  const handleConfigureIntegration = (integrationNameId: string) => {
    setActiveModal(integrationNameId);
  };

  const handleViewDocs = (docsUrl: string) => {
    window.open(docsUrl, '_blank');
  };

  const selectedIntegration = activeModal
    ? integrationCards.find(i => i.nameId === activeModal)
    : null;

  if (isLoadingCompanies || isLoadingIntegrations) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D72FF]" />
      </div>
    );
  }

  return (
    <div className="py-6 space-y-6">
      {/* Barra de busca e filtros */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full md:w-auto">
          <TabsList>
            {integrationsDataTabs.map((tab) => (
              <TabsTrigger key={tab.type} value={tab.type}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Input
          type="text"
          placeholder={translateInterfaceText("Buscar integração...")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-64"
        />
      </div>

      {/* Grid de Integrações - 4 colunas */}
      {filteredIntegrations.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-500">{translateInterfaceText("Nenhuma integração encontrada.")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredIntegrations.map((integration) => (
            <Card
              key={integration.nameId}
              className="bg-white border border-[#E4E7EC] rounded-[14px] p-6
                         shadow-[0_2px_6px_rgba(0,0,0,0.04)]
                         hover:shadow-[0_6px_16px_rgba(0,0,0,0.06)]
                         hover:border-[#2D72FF]
                         hover:-translate-y-0.5
                         transition-all duration-150 ease-out
                         flex flex-col gap-[18px]"
            >
              {/* Logo */}
              <div className="w-[62px] h-[62px]
                              flex items-center justify-center
                              
                              overflow-hidden">
                <img
                  src={integration.logo}
                  alt={integration.name}
                  className="w-auto h-auto object-contain p-2"
                />
              </div>

              <div className="flex flex-col gap-2 flex-1">
                {/* Nome */}
                <h3 className="text-[16px] font-semibold text-[#111827] leading-tight">
                  {integration.name}
                </h3>

                {/* Descrição */}
                <p className="text-[13px] text-[#6B7280] leading-[1.45] line-clamp-2">
                  {integration.description}
                </p>
              </div>

              {/* Badge Status */}
              {integration.isConnected ? (
                <div className="inline-flex items-center gap-1.5 self-start
                                bg-[#ECFDF5] rounded-lg px-2.5 py-1 text-[12px] text-[#059669]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
                  {translateInterfaceText("Conectado")}</div>
              ) : (
                <div className="inline-flex items-center gap-1.5 self-start
                                bg-[#F3F4F6] rounded-lg px-2.5 py-1 text-[12px] text-[#6B7280]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#9CA3AF]"></span>
                  {translateInterfaceText("Não conectado")}</div>
              )}

              {/* Botões */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleConfigureIntegration(integration.nameId)}
                  className="flex-1 h-10 rounded-[12px]
                             border border-[#DADDE4] bg-white
                             text-[14px] font-medium text-[#2D72FF]
                             hover:bg-[#2D72FF] hover:border-[#2D72FF]
                             transition-all duration-150"
                >
                  {translateInterfaceText("Configurar")}</Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleViewDocs(integration.docsUrl)}
                  className="h-10 w-10 rounded-[12px]
                             hover:bg-[#F3F6FF]
                             transition-all duration-150"
                  title={translateInterfaceText("Ver documentação")}
                >
                  <ExternalLink className="h-4 w-4 text-[#6B7280]" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Configuração */}
      <Dialog open={!!activeModal} onOpenChange={() => setActiveModal(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[18px] font-semibold text-[#111827]">
              {translateInterfaceText("Configurar integração com")}{" "}{selectedIntegration?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {activeModal && renderIntegrationForm(activeModal)}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActiveModal(null)}
              className="flex-1"
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button
              type="submit"
              form="integration-form"
              className="flex-1 bg-[#2D72FF] hover:bg-[#1B52CC]"
            >
              {translateInterfaceText("Salvar")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
